import type { IAPI_ChannelConfigParams } from '@core/api'
import { getGlobalConfig } from '@core/globalConfig'
import { pruneNilValues } from '@core/object'
import type {
  IAllWidgetParams,
  IAnyWidgetParams,
  IWidgetInitialParams,
} from '@core/types'
import { ThumbnailShapeEnum, WidgetTypesEnum } from '@core/types'
import { parseQueryParams } from '@core/window'

import { aliasWidgetParams } from './aliasWidgetParams'
import { debugParams } from './debugParams'
import { isStories } from './isStories'
import { sanitizeWidgetParams } from './sanitizeWidgetParams'
import { urlWidgetParams } from './urlWidgetParams'

interface Attrs {
  initialParams?: IWidgetInitialParams // Params set by widget (embed feed, storyblock, etc.)
  fetchedParams?: Partial<IAPI_ChannelConfigParams> // Params set in naboo admin (fetched remotely)
  experimentParams?: Partial<IAllWidgetParams> // Params from backend A/B testing experiments
  widgetParams?: Partial<IAnyWidgetParams> // Params set by publisher as widget attributes
  defaultParams?: Partial<IAllWidgetParams>
  fromCohortParams?: { playlist?: string } // existing params that may have been set via actions#setCohortParams
  embedConfigParams?: Partial<IAllWidgetParams>
}

/**
 * Resolves params in particular order and returns store structure.
 */
export function resolveStoreParams(
  state: Attrs, // On store creation initialState is provided
  attrs: Attrs,
) {
  const { defaultParams, fromCohortParams } = state

  // Initial widget specific params
  const initialParams = attrs?.initialParams ?? state.initialParams
  // Fetched as channel.config from API source
  const fetchedParams = attrs?.fetchedParams
    ? sanitizeWidgetParams(
        aliasWidgetParams(pruneNilValues(attrs.fetchedParams)),
        {
          skip: ['max_videos', 'mode'], // TODO(CS-2991, CS-6770): Remove this when we remove max_videos from channel.config
        },
      )
    : state.fetchedParams
  // Params from backend A/B testing experiments
  const experimentParams = attrs?.experimentParams
    ? sanitizeWidgetParams(attrs.experimentParams)
    : state.experimentParams
  // Widget params specified via integration code as a html attributes
  const widgetParams = attrs?.widgetParams
    ? sanitizeWidgetParams(aliasWidgetParams(attrs.widgetParams))
    : state.widgetParams
  // Specified by a url query params `fwparam_{name}` likely to quickly override other params
  const urlParams = sanitizeWidgetParams(urlWidgetParams(parseQueryParams()))
  // Specified by a `data-{name}` html attributes in a <script> tag to provide
  // global config params to all widgets on a page
  const globalConfigParams = sanitizeWidgetParams(
    aliasWidgetParams(getGlobalConfig() ?? {}),
  )

  const embedConfigParams = attrs?.embedConfigParams
    ? sanitizeWidgetParams(aliasWidgetParams(attrs.embedConfigParams))
    : state.embedConfigParams

  const params: IAllWidgetParams = {
    ...defaultParams,
    ...initialParams,
    ...fetchedParams,
    ...experimentParams,
    ...embedConfigParams,
    ...globalConfigParams,
    ...widgetParams,
    ...urlParams,
    ...fromCohortParams,
  }

  if (params.type === WidgetTypesEnum.EMBED_FEED) {
    if (params.mode === 'stories') {
      params.mode = 'row'
      params.thumbnail_shape ??= ThumbnailShapeEnum.CIRCLE
    }

    if (isStories(params)) {
      params.mode = 'row'
    }
  }

  debugParams(
    {
      defaultParams,
      initialParams,
      embedConfigParams,
      experimentParams,
      fetchedParams,
      globalConfigParams,
      widgetParams,
      urlParams,
    },
    params,
  )

  return {
    params,
    defaultParams,
    initialParams,
    fetchedParams,
    experimentParams,
    widgetParams,
    urlParams,
    globalConfigParams,
    embedConfigParams,
    fromCohortParams,
  }
}
